import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { tryListTopics, tryGetBundle } from "@/lib/api";
import TryProgressIndicator from "@/components/try/TryProgressIndicator";
import TryTopicGrid from "@/components/try/TryTopicGrid";
import TryRecapScreen from "@/components/try/TryRecapScreen";
import TryQuestionScreen from "@/components/try/TryQuestionScreen";
import TryTopicComplete from "@/components/try/TryTopicComplete";

export default function TryLeftPanel({ mode, onSignal = () => {} }) {
  const [topics, setTopics] = useState([]);
  const [guidedSequence, setGuidedSequence] = useState([]);
  const [guidedIndex, setGuidedIndex] = useState(0);
  const [bundle, setBundle] = useState(null);
  const [stage, setStage] = useState("loading"); // loading | grid | recap | warmup | practice | complete | error
  const [qIndex, setQIndex] = useState(0);
  const [topicAttempts, setTopicAttempts] = useState([]);

  const loadTopic = async (topic) => {
    setStage("loading");
    try {
      const b = await tryGetBundle(topic.grade, topic.subject, topic.sub_topic, topic.practice_count);
      setBundle(b);
      setQIndex(0);
      setTopicAttempts([]);
      setStage("recap");
      onSignal({
        type: "topic_activated",
        kind: "topic",
        grade: topic.grade,
        subject: topic.subject,
        topic: topic.topic,
        sub_topic: topic.sub_topic,
      });
    } catch {
      setStage("error");
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const topicsRes = await tryListTopics();
        if (cancelled) return;

        // Free Explore — show the topic grid, never auto-load a guided topic.
        if (mode === "free") {
          setTopics(topicsRes.topics || []);
          setStage("grid");
          return;
        }

        // Guided Demo — auto-load the first topic in the sequence.
        const sequence = topicsRes.guided_sequence || [];
        setGuidedSequence(sequence);
        setGuidedIndex(0);
        const topic = sequence[0];
        if (!topic) { setStage("error"); return; }
        await loadTopic(topic);
      } catch {
        if (!cancelled) setStage("error");
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  if (stage === "loading") {
    return (
      <div className="h-full flex items-center justify-center text-slate-300" data-testid="try-left-loading">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }
  if (stage === "error") {
    return (
      <div className="h-full flex items-center justify-center text-slate-400 text-sm text-center px-6" data-testid="try-left-error">
        Couldn't load this topic right now. Please refresh and try again.
      </div>
    );
  }

  const warmup = bundle?.warmup || [];
  const practice = bundle?.practice || [];

  const handleRecapProceed = () => {
    setQIndex(0);
    setStage(warmup.length > 0 ? "warmup" : "practice");
  };

  const handleWarmupContinue = () => {
    if (qIndex + 1 < warmup.length) setQIndex((n) => n + 1);
    else { setQIndex(0); setStage("practice"); }
  };

  const handlePracticeContinue = () => {
    if (qIndex + 1 < practice.length) setQIndex((n) => n + 1);
    else {
      setStage("complete");
      const warmupAttempts = topicAttempts.filter((a) => a.kind === "warmup");
      const practiceAttempts = topicAttempts.filter((a) => a.kind === "practice");
      onSignal({
        type: "topic_complete",
        subTopic: bundle.sub_topic,
        subject: bundle.subject,
        practiceAttempts,
        warmupPassed: warmupAttempts.every((a) => a.result?.is_correct),
      });
    }
  };

  const isLastGuidedTopic = guidedIndex + 1 >= guidedSequence.length;

  const handleContinueNextTopic = async () => {
    const nextIndex = isLastGuidedTopic ? 0 : guidedIndex + 1;
    setGuidedIndex(nextIndex);
    await loadTopic(guidedSequence[nextIndex]);
  };

  const handleAnswered = (payload) => {
    const record = { type: "attempt_recorded", ...payload };
    setTopicAttempts((prev) => [...prev, record]);
    onSignal(record);
  };

  return (
    <div className="h-full flex flex-col" data-testid="try-left-panel-content">
      {stage !== "grid" && <TryProgressIndicator stage={stage} />}
      <div className="flex-1 mt-6">
        {stage === "grid" && <TryTopicGrid topics={topics} onSelect={loadTopic} />}

        {stage === "recap" && <TryRecapScreen recap={bundle.recap} onProceed={handleRecapProceed} />}

        {stage === "warmup" && (
          <TryQuestionScreen
            key={warmup[qIndex].record_id}
            question={warmup[qIndex]}
            stageLabel="Warm Up"
            kind="warmup"
            progress={{ current: qIndex + 1, total: warmup.length }}
            onContinue={handleWarmupContinue}
            onAnswered={handleAnswered}
          />
        )}

        {stage === "practice" && (
          <TryQuestionScreen
            key={practice[qIndex].record_id}
            question={practice[qIndex]}
            stageLabel="Practice"
            kind="practice"
            progress={{ current: qIndex + 1, total: practice.length }}
            onContinue={handlePracticeContinue}
            onAnswered={handleAnswered}
          />
        )}

        {stage === "complete" && (
          <TryTopicComplete
            mode={mode}
            topicLabel={`${bundle.subject} · ${bundle.sub_topic}`}
            isLastTopic={isLastGuidedTopic}
            onContinue={handleContinueNextTopic}
            onPickAnother={() => setStage("grid")}
          />
        )}
      </div>
    </div>
  );
}
